// Vendored from quill-languagetool v1.0.7 — adapted for Quill v2
// Key changes:
// - Removed nanohtml dependency (replaced with standard DOM APIs)
// - Removed separate quill-delta dependency (use Quill's built-in Delta)
// - Fixed SuggestionBlot to extend blots/inline instead of formats/bold
// - Fixed optimize() to call super.optimize() (required by Quill v2)
// - Replaced popper.js with simple absolute positioning
// - Removed CSS import (CSS is injected via SCSS)

import type Quill from "quill";
import debug from "./debug";
import { SuggestionBoxes } from "./SuggestionBoxes";
import createSuggestionBlotForQuillInstance from "./SuggestionBlot";
import PopupManager from "./PopupManager";
import { LanguageToolApi, LanguageToolApiParams, MatchesEntity } from "./types";
import LoadingIndicator from "./LoadingIndicator";

export type QuillLanguageToolParams = {
  server: string;
  language: string;
  disableNativeSpellcheck: boolean;
  cooldownTime: number;
  showLoadingIndicator: boolean;
  apiOptions?: Partial<LanguageToolApiParams>;
};

/**
 * QuillLanguageTool is a Quill plugin that provides spellchecking and grammar checking
 * using the LanguageTool API.
 */
export class QuillLanguageTool {
  static DEFAULTS: QuillLanguageToolParams = {
    server: "https://languagetool.org/api",
    language: "en-US",
    disableNativeSpellcheck: true,
    cooldownTime: 3000,
    showLoadingIndicator: true,
    apiOptions: {},
  };

  protected typingCooldown?: ReturnType<typeof setTimeout>;
  protected loopPreventionCooldown?: ReturnType<typeof setTimeout>;
  protected boxes = new SuggestionBoxes(this);
  protected popups = new PopupManager(this);
  protected loader = new LoadingIndicator(this);
  public matches: MatchesEntity[] = [];

  constructor(
    public quill: Quill,
    public params: QuillLanguageToolParams,
  ) {
    debug("Attaching QuillLanguageTool to Quill instance", quill);
    this.quill.on("text-change", (_delta: any, _oldDelta: any, source: string) => {
      if (source === "user") {
        this.onTextChange();
      }
    });
    this.checkSpelling();
    this.disableNativeSpellcheckIfSet();
  }

  private disableNativeSpellcheckIfSet() {
    if (this.params.disableNativeSpellcheck) {
      this.quill.root.setAttribute("spellcheck", "false");
    }
  }

  private onTextChange() {
    if (this.loopPreventionCooldown) {
      return;
    }
    if (this.typingCooldown) {
      clearTimeout(this.typingCooldown);
    }
    this.typingCooldown = setTimeout(() => {
      debug("User stopped typing, checking spelling");
      this.checkSpelling();
    }, this.params.cooldownTime);
  }

  public async reloadBoxes() {
    this.boxes.removeSuggestionBoxes();
    this.boxes.addSuggestionBoxes();
  }

  private async checkSpelling() {
    debug("Removing existing suggestion boxes");
    this.boxes.removeSuggestionBoxes();

    if (document.querySelector("lt-toolbar")) {
      debug("LanguageTool is installed as extension, not checking");
      return;
    }

    debug("Checking spelling");
    this.loader.startLoading();

    try {
      const json = await this.getLanguageToolResults();
      if (json && json.matches) {
        this.matches = json.matches;
        debug("Adding suggestion boxes");
        this.boxes.addSuggestionBoxes();
      } else {
        debug("No matches found");
        this.matches = [];
      }
    } catch (e) {
      debug("Error during spell check", e);
      this.matches = [];
    }

    this.loader.stopLoading();
  }

  private async getLanguageToolResults(): Promise<LanguageToolApi | null> {
    const params = this.getApiParams();
    try {
      const response = await fetch(this.params.server + "/v2/check", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        mode: "cors",
        body: params,
      });
      const json = (await response.json()) as LanguageToolApi;
      return json;
    } catch (e) {
      return null;
    }
  }

  private getApiParams(): string {
    const paramsObject: Record<string, any> = {
      text: this.quill.getText(),
      language: this.params.language,
      ...this.params.apiOptions,
    };
    return Object.keys(paramsObject)
      .map((key) => `${key}=${encodeURIComponent(paramsObject[key])}`)
      .join("&");
  }

  public preventLoop() {
    if (this.loopPreventionCooldown) {
      clearTimeout(this.loopPreventionCooldown);
    }
    this.loopPreventionCooldown = setTimeout(() => {
      this.loopPreventionCooldown = undefined;
    }, 100);
  }
}

/**
 * Register all QuillLanguageTool modules with Quill.
 *
 * This needs access to the exact Quill static instance
 * you will be using in your application.
 *
 * Example:
 * ```
 * import Quill from "quill";
 * import registerQuillLanguageTool from "./quill-languagetool";
 * registerQuillLanguageTool(Quill);
 * ```
 */
export default function registerQuillLanguageTool(Quill: any) {
  debug("Registering QuillLanguageTool module for Quill instance");
  Quill.register({
    "modules/languageTool": QuillLanguageTool,
    "formats/ltmatch": createSuggestionBlotForQuillInstance(Quill),
  });
}

export { getCleanedHtml, removeSuggestionBoxes } from "./SuggestionBoxes";
